import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const BASE_URL = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5001';

const STYLES = `
  .sl-root {
    --bg: #F2EDE6; --surface: #FFF; --ink: #1C1814; --ink2: #6B6560;
    --ink3: #A8A3A0; --accent: #1A8A7A; --accent-dark: #0F5F54;
    --gold: #D4A853; --border: #E4DED6; --r: 20px;
    --shadow: 0 4px 24px rgba(28,24,20,.10);
    font-family: system-ui,-apple-system,'Segoe UI',sans-serif;
    font-size: 15px; line-height: 1.5; color: var(--ink);
    background: var(--bg); min-height: 100svh;
    display: flex; flex-direction: column; align-items: center;
    justify-content: center; padding: 24px 16px 48px;
  }
  @media(prefers-color-scheme:dark){
    .sl-root { --bg:#0D1614; --surface:#182220; --ink:#EDE8E2; --ink2:#8C8780;
      --ink3:#5A5550; --accent:#22B5A4; --accent-dark:#40D4C4;
      --gold:#E0B96A; --border:#2A3330; }
  }
  .sl-root * { box-sizing: border-box; }
  .sl-root button, .sl-root a { cursor: pointer; text-decoration: none; }

  .sl-card {
    width: 100%; max-width: 420px;
    animation: sl-in .25s ease both;
  }
  @keyframes sl-in { from { opacity:0; transform:translateY(14px) } to { opacity:1; transform:none } }

  /* Header */
  .sl-header { text-align: center; margin-bottom: 32px; }
  .sl-avatar {
    width: 72px; height: 72px; border-radius: 22px;
    background: var(--accent); color: #fff;
    display: flex; align-items: center; justify-content: center;
    font-size: 32px; font-weight: 800; margin: 0 auto 16px;
    box-shadow: 0 8px 24px rgba(26,138,122,.3);
  }
  .sl-salon-name {
    font-family: 'Garamond','Georgia',serif;
    font-size: 28px; font-weight: normal; color: var(--ink); margin: 0 0 6px;
  }
  .sl-salon-sub { font-size: 13px; color: var(--ink3); }

  /* Options */
  .sl-options { display: flex; flex-direction: column; gap: 14px; }

  .sl-option {
    background: var(--surface); border: 2px solid var(--border);
    border-radius: var(--r); padding: 22px 24px;
    display: flex; align-items: center; gap: 18px;
    box-shadow: var(--shadow);
    transition: transform .18s, border-color .18s, box-shadow .18s;
    color: inherit;
  }
  .sl-option:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 32px rgba(28,24,20,.14);
  }
  .sl-option.booking:hover  { border-color: var(--accent); }
  .sl-option.shop:hover     { border-color: var(--gold); }

  .sl-option-icon {
    width: 52px; height: 52px; border-radius: 16px; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center; font-size: 26px;
  }
  .sl-option.booking .sl-option-icon { background: #E8F4F2; }
  .sl-option.shop    .sl-option-icon { background: #FBF5E8; }

  .sl-option-body { flex: 1; }
  .sl-option-title { font-weight: 700; font-size: 16px; color: var(--ink); margin: 0 0 3px; }
  .sl-option-desc  { font-size: 13px; color: var(--ink2); margin: 0; }

  .sl-arrow { color: var(--ink3); font-size: 20px; flex-shrink: 0; }

  /* Divider */
  .sl-divider {
    display: flex; align-items: center; gap: 10px;
    color: var(--ink3); font-size: 12px; font-weight: 600;
    text-transform: uppercase; letter-spacing: .08em;
    margin: 2px 0;
  }
  .sl-divider::before, .sl-divider::after {
    content: ''; flex: 1; height: 1px; background: var(--border);
  }

  /* Spinner */
  .sl-spin { width:36px; height:36px; border:3px solid var(--border);
    border-top-color:var(--accent); border-radius:50%;
    animation:sl-spin .8s linear infinite; margin:0 auto; }
  @keyframes sl-spin { to { transform:rotate(360deg) } }
`;

export default function SalonLandingPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [salon,   setSalon]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');

  useEffect(() => {
    axios.get(`${BASE_URL}/booking/${slug}`)
      .then(r => setSalon(r.data.data.salon))
      .catch(() => setError('Salon introuvable.'))
      .finally(() => setLoading(false));
  }, [slug]);

  const initial = salon?.nom ? salon.nom.charAt(0).toUpperCase() : '✂';

  return (
    <>
      <style>{STYLES}</style>
      <div className="sl-root">

        {loading && <div className="sl-spin" />}

        {!loading && error && (
          <div style={{ textAlign:'center', color:'var(--ink2)' }}>
            <p style={{ fontSize:40, marginBottom:12 }}>🔗</p>
            <p style={{ fontWeight:700 }}>{error}</p>
          </div>
        )}

        {!loading && salon && (
          <div className="sl-card">

            {/* En-tête salon */}
            <div className="sl-header">
              <div className="sl-avatar">{initial}</div>
              <h1 className="sl-salon-name">{salon.nom}</h1>
              {salon.adresse && <p className="sl-salon-sub">📍 {salon.adresse}</p>}
            </div>

            <div className="sl-options">

              {/* Réservation */}
              <a href={`/booking/${slug}`} className="sl-option booking">
                <div className="sl-option-icon">✂️</div>
                <div className="sl-option-body">
                  <p className="sl-option-title">Prendre un rendez-vous</p>
                  <p className="sl-option-desc">Choisissez votre service et votre créneau en ligne</p>
                </div>
                <span className="sl-arrow">›</span>
              </a>

              {/* Boutique - seulement si shopSlug existe */}
              {salon.shopSlug && (
                <>
                  <div className="sl-divider">ou</div>
                  <a href={`/shop/${salon.shopSlug}`} className="sl-option shop">
                    <div className="sl-option-icon">🛍️</div>
                    <div className="sl-option-body">
                      <p className="sl-option-title">Notre boutique</p>
                      <p className="sl-option-desc">Découvrez et commandez nos produits en ligne</p>
                    </div>
                    <span className="sl-arrow">›</span>
                  </a>
                </>
              )}

            </div>
          </div>
        )}

      </div>
    </>
  );
}
